import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import clsx from 'clsx';
import { DRAWER_STACK, INVOICE_SUBTYPES, INVOICE_TYPES } from 'Utils/constants';
import useCurrencies from 'Hooks/useCurrencies';
import { getCurrencySymbol, formatDateISO } from 'Utils/formatters';
import Skeleton from 'Components/Atoms/Skeleton/Skeleton';
import Icon from 'Components/Atoms/Icon/Icon';
import { useTranslation } from 'react-i18next';
import useLocale from 'Hooks/useLocale';
import { Fill } from 'nano-slots';
import FormProvider, {
    RHFSelectMultipleTags,
    RHFSelectProduct,
    RHFSelectContact,
    RHFSelect,
    RHFInput,
    RHFTextarea,
    RHFDatePicker,
    RHFInputCurrency,
} from 'Components/Organisms/HookForm';
import Divider from 'Components/Atoms/Divider/Divider';
import Button from 'Components/Atoms/Button/Button';
import useDrawerStack from 'Hooks/useDrawerStack';
import { getContactByTaxId } from 'Services/invoices';
import { useQueryClient } from '@tanstack/react-query';
import useInvoiceContactSuggestions from 'Hooks/useInvoiceContactSuggestions';
import SuggestionButton from 'Components/Molecules/SuggestionButton/SuggestionButton';
import useInfiniteAiSuggestions from 'Hooks/useInfiniteAiSuggestions';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import InvoicesTaxes from 'Pages/Invoices/InvoicesTaxes.jsx';
import { cn } from 'Utils/cn';
import OutlineAi from 'Components/Atoms/OutlineIcons/OutlineAi';
import InvoiceDuplicateWarning from 'Components/Molecules/InvoiceDuplicateWarning/InvoiceDuplicateWarning';
import InvoiceTitle from 'Components/Atoms/InvoiceTitle/InvoiceTitle';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineTrash from 'Components/Atoms/OutlineIcons/OutlineTrash';

const formatDateValue = (date) => {
    return date ? formatDateISO(date) : '';
};

export default function InvoiceEditorDrawerFormFullData(props) {
    const { onCancel, invoice, onSubmit, className, submitText, cancelText, showDelete, onClose, onError } = props;

    const { open } = useDrawerStack();
    const { acceptSuggestion } = useInfiniteAiSuggestions();

    const [processing, setProcessing] = useState(false);
    const [autoCheckLoading, setAutoCheckLoading] = useState(false);
    const [isAiCalculatedContact, setIsAiCalculatedContact] = useState(false);
    const autoCheckDoneRef = useRef(false);
    const { currencies, isLoading: isCurrenciesLoading } = useCurrencies();

    const { t } = useTranslation('invoices');
    const locale = useLocale();
    const queryClient = useQueryClient();
    const { data, isLoading: isContactSuggestionsLoading } = useInvoiceContactSuggestions(invoice);

    const defaultSubmitText = t('accept');
    const submitButtonText = submitText || defaultSubmitText;

    const isDisabled = invoice.integration_id !== null;

    const currenciesOptions = useMemo(
        () =>
            currencies.map((c) => ({ value: c.code, label: c.name + ' (' + getCurrencySymbol(locale, c.code) + ')' })),
        [currencies, locale],
    );

    const typeOptions = useMemo(
        () => [
            { value: INVOICE_TYPES.SALE, label: t('sale') },
            { value: INVOICE_TYPES.PURCHASE, label: t('purchase') },
        ],
        [t],
    );

    const subtypeOptions = useMemo(
        () => [
            { value: INVOICE_SUBTYPES.STANDARD, label: t('invoice-standard') },
            { value: INVOICE_SUBTYPES.STANDARD_CORRECTIVE, label: t('invoice-standard-corrective') },
            { value: INVOICE_SUBTYPES.SIMPLIFIED, label: t('invoice-simplified') },
            { value: INVOICE_SUBTYPES.SIMPLIFIED_CORRECTIVE, label: t('invoice-simplified-corrective') },
            { value: INVOICE_SUBTYPES.QUOTE, label: t('invoice-quote') },
            { value: INVOICE_SUBTYPES.PROFORMA, label: t('invoice-proforma') },
            { value: INVOICE_SUBTYPES.DELIVERY_NOTE, label: t('invoice-delivery-note') },
        ],
        [t],
    );

    const invoiceSchema = yup.object().shape({
        date_emission: yup
            .date()
            .typeError(t('value-invalid'))
            .min('1969-11-13', t('date-is-too-early'))
            .max('2100-01-01', t('date-is-too-late'))
            .required(t('field-required')),
        date_expiration: yup
            .date()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .typeError(t('value-invalid'))
            .min(yup.ref('date_emission'), t('expiration-before-emission'))
            .max('2100-01-01', t('date-is-too-late'))
            .nullable(),
        date_operation: yup
            .date()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .typeError(t('value-invalid'))
            .min('1969-11-13', t('date-is-too-early'))
            .max('2100-01-01', t('date-is-too-late'))
            .nullable()
            .notRequired(),
        date_payment: yup
            .date()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .typeError(t('value-invalid'))
            .min('1969-11-13', t('date-is-too-early'))
            .max('2100-01-01', t('date-is-too-late'))
            .nullable(),
        amount: yup.number().typeError(t('value-invalid')).required(t('field-required')),
        amount_subtotal: yup.number().typeError(t('value-invalid')).required('positive-value-required'),
        amount_reimbursable: yup.number().typeError(t('value-invalid')),
        description: yup.string().max(256, t('max-characters')),
        identifier: yup.string().max(256, t('max-characters')),
        invoice_party_id: yup.string().required(t('field-required')),
        type: yup.string().required(t('field-required')),
        subtype: yup.string().required(t('field-required')),
        taxes: yup.array().of(
            yup.object().shape({
                tax_id: yup.string().required(t('field-required')),
                amount_base: yup
                    .number()
                    .transform((value, originalValue) => (originalValue === '' ? 0 : Number(originalValue))),
                amount_tax: yup
                    .number()
                    .transform((value, originalValue) => (originalValue === '' ? 0 : Number(originalValue))),
            }),
        ),
    });

    const methods = useForm({
        resolver: yupResolver(invoiceSchema),
        defaultValues: {
            type: invoice.type || '',
            subtype: invoice.subtype || '',
            identifier: invoice.identifier || '',
            date_emission: formatDateValue(invoice.date_emission),
            date_expiration: invoice.date_expiration ? formatDateValue(invoice.date_expiration) : '',
            date_operation: invoice.date_operation ? formatDateValue(invoice.date_operation) : '',
            date_payment: invoice.date_payment ? formatDateValue(invoice.date_payment) : '',
            amount: invoice.amount ?? '',
            invoice_party_id: invoice.invoice_party_id || null,
            amount_subtotal: invoice.amount_subtotal ?? '',
            amount_reimbursable: invoice.amount_reimbursable ?? '',
            description: invoice.description || '',
            currency: invoice.currency || 'EUR',
            product_id: invoice.product_id || '',
            tags_ids: invoice.tags_ids || [],
            taxes: invoice.taxes || [],
        },
    });

    const { handleSubmit, setValue, watch, getValues } = methods;
    const type = watch('type');

    const submitForm = async (data) => {
        setProcessing(true);
        await onSubmit({
            type: data.type,
            subtype: data.subtype,
            currency: data.currency,
            date_emission: formatDateISO(data.date_emission),
            product_id: data.product_id === '' ? null : data.product_id,
            description: data.description === '' ? null : data.description,
            identifier: data.identifier === '' ? null : data.identifier,
            tags_ids: data.tags_ids,
            date_expiration: data.date_expiration ? formatDateISO(data.date_expiration) : null,
            date_operation: data.date_operation ? formatDateISO(data.date_operation) : null,
            date_payment: data.date_payment ? formatDateISO(data.date_payment) : null,
            invoice_party_id: data.invoice_party_id,
            amount: data.amount,
            amount_subtotal: data.amount_subtotal,
            amount_reimbursable: data.amount_reimbursable,
            taxes: data.taxes || [],
        });
        setProcessing(false);
    };

    const handleCreateContact = () => {
        open(DRAWER_STACK.CONTACT_EDITOR_WITH_INVOICE_PREVIEW, {
            contact: {},
            title: t('create-contact'),
            onSubmit: async (savedContact) => {
                await queryClient.invalidateQueries({ queryKey: ['invoices'] });
                if (savedContact) {
                    setValue('invoice_party_id', savedContact.id);
                    if (Array.isArray(savedContact.categories_ids) && savedContact.categories_ids.length > 0) {
                        const prevTags = getValues('tags_ids') || [];
                        const newTags = [...prevTags];
                        savedContact.categories_ids.forEach((categoryId) => {
                            if (!newTags.includes(categoryId)) {
                                newTags.push(categoryId);
                            }
                        });
                        setValue('tags_ids', newTags);
                    }
                }
            },
        });
    };

    const handleDelete = () => {
        open(DRAWER_STACK.INVOICE_DELETE, {
            invoiceId: invoice.id,
            onSubmit: async () => {
                onClose();
            },
        });
    };

    const handleViewOriginalInvoice = () => {
        open(DRAWER_STACK.INVOICE_QUICK_EDITOR, {
            invoiceId: invoice?.invoice_duplicated_id,
            title: <InvoiceTitle invoice={invoice} />,
        });
    };

    const ClickSuggestionHandle = (type, suggestion) => {
        const newType = type === 'receiver' ? INVOICE_TYPES.SALE : INVOICE_TYPES.PURCHASE;
        setValue('type', newType);

        if (suggestion.contact.id) {
            setValue('invoice_party_id', suggestion.contact.id);
        } else {
            open(DRAWER_STACK.CONTACT_EDITOR_WITH_INVOICE_PREVIEW, {
                contact: suggestion.contact,
                title: t('create-contact'),
                onSubmit: async (savedContact) => {
                    if (savedContact) {
                        setValue('invoice_party_id', savedContact.id);
                        const {
                            id,
                            type,
                            name,
                            categorization_keywords,
                            categories_ids,
                            tax_id,
                            email,
                            phone_number,
                            address,
                            city,
                            state,
                            postal_code,
                            country,
                        } = savedContact;

                        acceptSuggestion({
                            suggestionId: suggestion.id,
                            data: {
                                contact: {
                                    id,
                                    type,
                                    name,
                                    categorization_keywords,
                                    categories_ids,
                                    tax_id,
                                    email,
                                    phone_number,
                                    address,
                                    city,
                                    state,
                                    postal_code,
                                    country,
                                },
                            },
                        });

                        if (Array.isArray(savedContact.categories_ids) && savedContact.categories_ids.length > 0) {
                            const prevTags = getValues('tags_ids') || [];
                            const newTags = [...prevTags];
                            savedContact.categories_ids.forEach((categoryId) => {
                                if (!newTags.includes(categoryId)) {
                                    newTags.push(categoryId);
                                }
                            });
                            setValue('tags_ids', newTags);
                        }
                    }
                },
            });
        }
    };

    const invoicePartyId = watch('invoice_party_id');

    useEffect(() => {
        if (isContactSuggestionsLoading || autoCheckDoneRef.current || invoicePartyId || !data) return;
        autoCheckDoneRef.current = true;

        const receiverTaxId = data.receiver_suggestion?.contact?.tax_id;
        const supplierTaxId = data.supplier_suggestion?.contact?.tax_id;

        if (!receiverTaxId && !supplierTaxId) return;

        let cancelled = false;
        setAutoCheckLoading(true);

        const promises = [
            receiverTaxId ? getContactByTaxId(receiverTaxId).catch(() => null) : Promise.resolve(null),
            supplierTaxId ? getContactByTaxId(supplierTaxId).catch(() => null) : Promise.resolve(null),
        ];

        Promise.all(promises).then(([receiverContact, supplierContact]) => {
            if (cancelled) return;

            if (receiverContact && data.receiver_suggestion) {
                ClickSuggestionHandle('receiver', {
                    ...data.receiver_suggestion,
                    contact: { ...data.receiver_suggestion.contact, id: receiverContact.id },
                });
                setIsAiCalculatedContact(true);
            } else if (supplierContact && data.supplier_suggestion) {
                ClickSuggestionHandle('supplier', {
                    ...data.supplier_suggestion,
                    contact: { ...data.supplier_suggestion.contact, id: supplierContact.id },
                });
                setIsAiCalculatedContact(true);
            }

            setAutoCheckLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [isContactSuggestionsLoading, data, invoicePartyId]);

    const showErrorUpload = invoice.status === 'ERROR_UPLOAD';
    const errorMessages = {
        could_not_read: t('error-could-not-read'),
        file_to_big: t('error-file-to-big'),
    };

    const statusMessage = errorMessages[invoice.status_message?.description] || t('error-unknown');
    const currency = watch('currency');
    const currencySymbol = getCurrencySymbol(locale, currency);
    return (
        <FormProvider
            className={clsx('h-full flex flex-col', className)}
            methods={methods}
            onSubmit={handleSubmit(submitForm)}
        >
            <InvoiceDuplicateWarning
                invoice={invoice}
                onDelete={handleDelete}
                onViewOriginal={handleViewOriginalInvoice}
                onError={onError}
            />

            {showErrorUpload && (
                <Fill name="invoices-dialog-header">
                    <div className="bg-[rgba(255,195,123,0.36)] rounded-md p-3 flex gap-2 text-sm text-dark-blue items-center mb-3">
                        <div className="w-6 shrink-0 py-1">
                            <Icon name="circle-info" size={24} color="currentColor" />
                        </div>
                        <div>{statusMessage}</div>
                    </div>
                </Fill>
            )}

            <div className="grid gap-6 pt-4 pb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <RHFSelect
                        className="w-full"
                        disabled={isDisabled}
                        label={t('type')}
                        options={typeOptions}
                        name="type"
                    />

                    <RHFSelect
                        className="w-full"
                        disabled={isDisabled}
                        label={t('subtype')}
                        options={subtypeOptions}
                        name="subtype"
                    />
                </div>

                <div
                    className={cn(
                        'grid gap-1.5',
                        isAiCalculatedContact &&
                            '[&_[data-float-label]]:text-warning [&_[data-float-label]]:before:top-1/2',
                    )}
                >
                    <RHFSelectContact
                        disabled={isDisabled}
                        name="invoice_party_id"
                        label={t('contact')}
                        onAdd={handleCreateContact}
                        placeholder={t('select-contact-placeholder')}
                        className={cn(
                            isAiCalculatedContact &&
                                'border-warning ring-2 focus-visible:ring-2 ring-warning/20 focus-visible:border-warning',
                        )}
                        onFocus={() => {
                            if (isAiCalculatedContact) {
                                setIsAiCalculatedContact(false);
                            }
                        }}
                        onChange={(_contactId, contact) => {
                            setIsAiCalculatedContact(false);
                            if (Array.isArray(contact?.categories_ids) && contact?.categories_ids.length > 0) {
                                const prevTags = getValues('tags_ids') || [];
                                const newTags = [...prevTags];
                                contact.categories_ids.forEach((categoryId) => {
                                    if (!newTags.includes(categoryId)) {
                                        newTags.push(categoryId);
                                    }
                                });
                                setValue('tags_ids', newTags);
                            }
                        }}
                    />
                    {isAiCalculatedContact && invoicePartyId && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                            <OutlineAi size={16} />
                            <span>{t('ai-calculated-contact')}</span>
                        </div>
                    )}
                </div>

                {!invoicePartyId && (
                    <>
                        {isContactSuggestionsLoading || autoCheckLoading ? (
                            <div className="flex gap-3 mb-3">
                                <Skeleton variant="rounded" className="w-[170px]" height={91} />
                                <Skeleton variant="rounded" className="w-[170px]" height={91} />
                            </div>
                        ) : (
                            <div className="flex gap-3 mb-3 empty:hidden">
                                {data.receiver_suggestion?.contact && (
                                    <SuggestionButton
                                        title={data.receiver_suggestion.contact.name}
                                        descriptionItems={[data.receiver_suggestion.contact.tax_id]}
                                        onClick={() => ClickSuggestionHandle('receiver', data.receiver_suggestion)}
                                        actionText={
                                            data.receiver_suggestion.contact.id ? (
                                                t('select-contact')
                                            ) : (
                                                <>
                                                    <OutlineAdd color="currentColor" size={14} strokeWidth={2.5} />
                                                    {t('create-contact')}
                                                </>
                                            )
                                        }
                                    />
                                )}
                                {data.supplier_suggestion?.contact && (
                                    <SuggestionButton
                                        title={data.supplier_suggestion.contact.name}
                                        descriptionItems={[data.supplier_suggestion.contact.tax_id]}
                                        onClick={() => ClickSuggestionHandle('supplier', data.supplier_suggestion)}
                                        actionText={
                                            data.supplier_suggestion.contact.id ? (
                                                t('select-contact')
                                            ) : (
                                                <>
                                                    <OutlineAdd color="currentColor" size={14} strokeWidth={2.5} />
                                                    {t('create-contact')}
                                                </>
                                            )
                                        }
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}

                <Divider />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <RHFInputCurrency
                        disabled={isDisabled}
                        name="amount_subtotal"
                        prefix={currencySymbol}
                        label={t('subtotal')}
                    />
                    <RHFInputCurrency
                        disabled={isDisabled}
                        name="amount_reimbursable"
                        prefix={currencySymbol}
                        label={t('reimbursable')}
                    />
                </div>
                <RHFInputCurrency disabled={isDisabled} name="amount" prefix={currencySymbol} label={t('total')} />

                <InvoicesTaxes currency={currency} disabled={isDisabled} />
                <Divider />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <RHFInput disabled={isDisabled} name="identifier" label={t('invoice-number')} />

                    {isCurrenciesLoading ? (
                        <Skeleton variant="rounded" height={44} />
                    ) : (
                        <RHFSelect
                            enableFilter
                            disabled={isDisabled}
                            label={t('currency')}
                            options={currenciesOptions}
                            name="currency"
                            className="w-full"
                        />
                    )}
                </div>

                <RHFTextarea
                    disabled={isDisabled}
                    name="description"
                    label={t('description')}
                    placeholder={t('invoice-description-placeholder')}
                />

                <RHFDatePicker disabled={isDisabled} name="date_emission" label={t('emission-date')} />
                <RHFDatePicker
                    disabled={isDisabled}
                    name="date_operation"
                    label={t('verifactu-operation-date-label')}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <RHFDatePicker disabled={isDisabled} name="date_expiration" label={t('expiration-date')} />
                    <RHFDatePicker
                        name="date_payment"
                        label={
                            invoice.reconciled_status === 'RECONCILED' ? t('payment-date') : t('payment-date-expected')
                        }
                    />
                </div>

                <RHFSelectProduct
                    name="product_id"
                    label={type === INVOICE_TYPES.PURCHASE ? t('payment-account') : t('collection-account')}
                />

                <RHFSelectMultipleTags label={t('tags')} name="tags_ids" />
            </div>

            <div className="grow" />

            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button
                        variant="primary"
                        isBusy={processing}
                        type="submit"
                        className="grow"
                        testId="invoice-validation.accept"
                    >
                        {submitButtonText}
                    </Button>
                    <Button onClick={onCancel} className="grow" testId="invoice-validation.cancel">
                        {cancelText || t('cancel')}
                    </Button>
                    {showDelete && (
                        <IconButton
                            icon={<OutlineTrash />}
                            tooltip={t('delete')}
                            onClick={handleDelete}
                            className="text-danger"
                            testId="invoice-validation.delete"
                        />
                    )}
                </div>
            </div>
        </FormProvider>
    );
}

InvoiceEditorDrawerFormFullData.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    invoice: PropTypes.object.isRequired,
    className: PropTypes.string,
    submitText: PropTypes.node,
    cancelText: PropTypes.node,
    // Botón con icono junto al de cancelar para borrar la factura (pide confirmación y luego llama a onClose).
    showDelete: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};
